import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { env } from '@/lib/env';

/** Server returns avatar_url like "/auth/avatar/<id>?v=<stamp>" — relative to
 *  the API origin. We need to prefix with VITE_API_URL so <img> hits the
 *  correct host (api.4m-immo.com), not the web origin. */
function resolveAvatarUrl(src: string | null | undefined): string | null {
  if (!src) return null;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith('/')) {
    const base = env.VITE_API_URL.replace(/\/$/, '');
    return `${base}${src}`;
  }
  return src;
}

const PALETTE = [
  '#7a5a3a',
  '#1f3a52',
  '#4a3a2a',
  '#5e4a3a',
  '#3a4a3a',
  '#7a3a3a',
  '#5a4a6a',
  '#46403a',
  '#7a6a3a',
  '#3a5a5a',
];

export function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length]!;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

interface AvatarProps {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Optional image URL. Falls back to initials if missing or fails to load. */
  src?: string | null;
}

export function Avatar({ name, size = 'md', className, src }: AvatarProps) {
  const sizeCls = size === 'lg' ? 'fm-avatar lg' : size === 'sm' ? 'fm-avatar sm' : 'fm-avatar';
  const [errored, setErrored] = useState(false);
  const resolvedSrc = resolveAvatarUrl(src);

  // Reset error state when src changes (new upload, cache-bust)
  useEffect(() => {
    setErrored(false);
  }, [resolvedSrc]);

  if (resolvedSrc && !errored) {
    return (
      <div
        className={cn(sizeCls, 'overflow-hidden', className)}
        style={{ background: avatarColor(name) }}
        aria-label={name}
      >
        <img
          src={resolvedSrc}
          alt={name}
          onError={() => setErrored(true)}
          className="h-full w-full object-cover"
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(sizeCls, className)}
      style={{ background: avatarColor(name) }}
      aria-label={name}
    >
      {initials(name)}
    </div>
  );
}
